// ============================================================
// MEMORY LEAK DETECTOR — Fase 12.1
// Detecção proativa de leaks em runtime
// Monitora: heap, intervals, channels, stores, listeners
// ============================================================
import { obs } from '@/core/observability'
import { getRegistryCount, getOrphanedResources } from './intervalRegistry'
import { getChannelSnapshot } from './channelMonitor'

export type LeakSeverity = 'info' | 'warning' | 'critical'

export interface LeakReport {
  timestamp:   string
  leaks:       LeakEntry[]
  heapMB:      number
  heapGrowthMB: number
  intervals:   number
  timeouts:    number
  openChannels: number
  zombieChannels: number
  orphanedResources: number
  overallRisk: LeakSeverity
}

export interface LeakEntry {
  type:     string
  message:  string
  severity: LeakSeverity
  count?:   number
}

// ─── Config ──────────────────────────────────────────────
const THRESHOLDS = {
  intervals:       { warning: 15, critical: 30 },
  timeouts:        { warning: 20, critical: 40 },
  channels:        { warning: 10, critical: 20 },
  zombieChannels:  { warning: 2,  critical: 5  },
  heapGrowthMB:    { warning: 30, critical: 80 },
  orphaned:        { warning: 5,  critical: 15 },
}

// ─── State ────────────────────────────────────────────────
const _heapHistory: number[] = []
let   _lastReport:  LeakReport | null = null
let   _timer:       ReturnType<typeof setInterval> | null = null
const _listeners:   Array<(r: LeakReport) => void> = []

// ─── Main detector ────────────────────────────────────────
export function startLeakDetector(intervalMs = 30_000): () => void {
  if (_timer) clearInterval(_timer)
  _timer = setInterval(() => { void runLeakDetection() }, intervalMs)
  void runLeakDetection()

  return () => {
    if (_timer) { clearInterval(_timer); _timer = null }
  }
}

export function onLeakReport(fn: (r: LeakReport) => void): () => void {
  _listeners.push(fn)
  return () => {
    const i = _listeners.indexOf(fn)
    if (i !== -1) _listeners.splice(i, 1)
  }
}

export function getLastLeakReport(): LeakReport | null {
  return _lastReport
}

async function runLeakDetection(): Promise<void> {
  const leaks: LeakEntry[] = []

  // ── 1. Heap snapshot ───────────────────────────────────
  let heapMB      = 0
  let heapGrowthMB = 0
  if (typeof performance !== 'undefined' && (performance as any).memory) {
    const mem = (performance as any).memory as { usedJSHeapSize: number }
    heapMB = Math.round(mem.usedJSHeapSize / 1_048_576)
    _heapHistory.push(heapMB)
    if (_heapHistory.length > 20) _heapHistory.shift()  // ~10 min at 30s
    if (_heapHistory.length >= 4) {
      heapGrowthMB = heapMB - _heapHistory[0]!
      if (heapGrowthMB > THRESHOLDS.heapGrowthMB.critical) {
        leaks.push({ type: 'heap', message: `Heap crescendo: +${heapGrowthMB}MB`, severity: 'critical', count: heapGrowthMB })
      } else if (heapGrowthMB > THRESHOLDS.heapGrowthMB.warning) {
        leaks.push({ type: 'heap', message: `Heap crescendo: +${heapGrowthMB}MB`, severity: 'warning', count: heapGrowthMB })
      }
    }
  }

  // ── 2. Registry counts ─────────────────────────────────
  const counts = getRegistryCount()

  if (counts.interval >= THRESHOLDS.intervals.critical) {
    leaks.push({ type: 'interval', message: `${counts.interval} intervals ativos (leak?)`, severity: 'critical', count: counts.interval })
  } else if (counts.interval >= THRESHOLDS.intervals.warning) {
    leaks.push({ type: 'interval', message: `${counts.interval} intervals ativos`, severity: 'warning', count: counts.interval })
  }

  if (counts.timeout >= THRESHOLDS.timeouts.critical) {
    leaks.push({ type: 'timeout', message: `${counts.timeout} timeouts ativos`, severity: 'critical', count: counts.timeout })
  } else if (counts.timeout >= THRESHOLDS.timeouts.warning) {
    leaks.push({ type: 'timeout', message: `${counts.timeout} timeouts ativos`, severity: 'warning', count: counts.timeout })
  }

  // ── 3. Channel monitor ─────────────────────────────────
  const chanSnap = getChannelSnapshot()

  if (chanSnap.openCount >= THRESHOLDS.channels.critical) {
    leaks.push({ type: 'channel', message: `${chanSnap.openCount} channels abertos`, severity: 'critical', count: chanSnap.openCount })
  } else if (chanSnap.openCount >= THRESHOLDS.channels.warning) {
    leaks.push({ type: 'channel', message: `${chanSnap.openCount} channels abertos`, severity: 'warning', count: chanSnap.openCount })
  }

  if (chanSnap.zombies.length >= THRESHOLDS.zombieChannels.critical) {
    leaks.push({ type: 'zombie_channel', message: `${chanSnap.zombies.length} channels zumbi (>10min)`, severity: 'critical', count: chanSnap.zombies.length })
  } else if (chanSnap.zombies.length >= THRESHOLDS.zombieChannels.warning) {
    leaks.push({ type: 'zombie_channel', message: `${chanSnap.zombies.length} channels zumbi (>10min)`, severity: 'warning', count: chanSnap.zombies.length })
  }

  // ── 4. Orphaned resources ──────────────────────────────
  const orphaned = getOrphanedResources(300_000).length  // >5min
  if (orphaned >= THRESHOLDS.orphaned.critical) {
    leaks.push({ type: 'orphan', message: `${orphaned} recursos órfãos (>5min)`, severity: 'critical', count: orphaned })
  } else if (orphaned >= THRESHOLDS.orphaned.warning) {
    leaks.push({ type: 'orphan', message: `${orphaned} recursos órfãos (>5min)`, severity: 'warning', count: orphaned })
  }

  // ── Derive overall risk ────────────────────────────────
  const overallRisk: LeakSeverity = leaks.some(l => l.severity === 'critical')
    ? 'critical'
    : leaks.some(l => l.severity === 'warning')
    ? 'warning'
    : 'info'

  const report: LeakReport = {
    timestamp:         new Date().toISOString(),
    leaks,
    heapMB,
    heapGrowthMB,
    intervals:         counts.interval,
    timeouts:          counts.timeout,
    openChannels:      chanSnap.openCount,
    zombieChannels:    chanSnap.zombies.length,
    orphanedResources: orphaned,
    overallRisk,
  }

  _lastReport = report
  _listeners.forEach(fn => fn(report))

  if (overallRisk !== 'info') {
    obs.warn('leak_detector', `Memory risk: ${overallRisk}`, {
      heapMB,
      heapGrowthMB,
      leakCount: leaks.length,
    })
  }
}
